import React, { useState } from 'react'
import { listTablePrefs, TablePrefsParams } from 'app/constants'
import { pick } from 'ramda'
import { routes } from 'core/utils/routes'
import DocumentMeta from 'core/components/DocumentMeta'
import DataKeys from 'k8s/DataKeys'
import { ArrayElement } from 'core/actions/Action'
import ListContainer from 'core/containers/ListContainer'
import useListAction from 'core/hooks/useListAction'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import useGlobalParams from 'core/hooks/useGlobalParams'
import { createGridLinkCell } from 'core/elements/grid/cells/GridLinkCell'
import { GridViewColumn } from 'core/elements/grid/Grid'
import { SortingState } from 'core/elements/grid/hooks/useGridSorting'
import InferActionParams from 'core/actions/InferActionParams'
import { listLimitRanges } from './actions'
import { limitRangesSelector } from './selectors'
import { useSelector } from 'react-redux'
import usePluginRouter from 'core/hooks/usePluginRouter'
import { ILimitRangeDetailsPageTabs } from './model'
import ClusterNameCell from 'k8s/components/common/cells/ClusterNameCell'
import { durationBetweenDates } from 'utils/misc'
import { createResourceLabelsCell } from 'k8s/components/common/entity/labels-and-annotations/helpers'
import AddLimitRangeModal from './AddLimitRangeModal'
import { GridRowMenuItemSpec } from 'core/elements/grid/hooks/useGridRowMenu'
import DeleteLimitRangeDialog from './DeleteLimitRangeDialog'

type ModelDataKey = DataKeys.LimitRanges
type SelectorModel = ArrayElement<ReturnType<typeof limitRangesSelector>>
type ActionParams = InferActionParams<typeof listLimitRanges>
//@fixme using a type here because of https://github.com/microsoft/TypeScript/issues/15300
type Params = ActionParams & {
  masterNodeClusters: boolean
  healthyClusters: boolean
}

const requiredParams: Array<keyof ActionParams> = ['clusterId', 'namespace']
const defaultParams: Params & SortingState = {
  clusterId: null,
  namespace: null,
  masterNodeClusters: true,
  healthyClusters: true,
  orderBy: 'name',
  orderDirection: 'asc',
}

const usePrefParams = createUsePrefParamsHook<Params & TablePrefsParams>(
  'LimitRanges',
  listTablePrefs,
)

const searchTargets = ['name', 'clusterName']

export const getLimitRangesColumns = (pluginId = 'kubernetes'): GridViewColumn<SelectorModel>[] => {
  const routePath = pluginId === 'kubevirt' ? 'kubevirtConfiguration' : 'configuration'
  return [
    {
      key: 'name',
      label: 'Name',
      width: 'medium',
      CellComponent: createGridLinkCell({
        routeToFn: ({ clusterId, id }) =>
          routes[routePath].limitRanges.details.path({
            clusterId,
            id,
            tab: ILimitRangeDetailsPageTabs.Overview,
          }),
      }),
    },
    { key: 'clusterName', label: 'Cluster', width: 'medium', CellComponent: ClusterNameCell },
    { key: 'namespace', label: 'Namespace' },
    {
      key: 'created',
      label: 'Age',
      formatFn: (value) => durationBetweenDates({ labels: ['d'] })(value),
    },
    {
      key: 'labels',
      label: 'Labels',
      disableSorting: true,
      CellComponent: createResourceLabelsCell({ type: 'table', separator: '=' }),
    },
    {
      key: 'annotations',
      label: 'Annotations',
      disableSorting: true,
      CellComponent: createResourceLabelsCell({ type: 'table', separator: ': ' }),
    },
  ]
}

export default function LimitRangesListPage() {
  const { currentPluginId } = usePluginRouter()
  const routePath = currentPluginId === 'kubevirt' ? 'kubevirtConfiguration' : 'configuration'
  const { allParams: params, getParamsUpdater } = useGlobalParams(usePrefParams, defaultParams)
  const { message, loading, reload } = useListAction(listLimitRanges, {
    params,
    requiredParams,
  })
  const limitRanges = useSelector(limitRangesSelector)

  const columns = getLimitRangesColumns(currentPluginId)

  const [selectedLimitRange, setSelectedLimitRange] = useState<SelectorModel>(null)
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false)

  const rowMenuItems: Array<GridRowMenuItemSpec<SelectorModel>> = [
    {
      label: 'Delete',
      icon: 'trash-alt',
      handleClick: (limitRange) => {
        setSelectedLimitRange(limitRange)
        setShowDeleteDialog(true)
      },
      refreshAfterSuccess: true,
    },
  ]

  return (
    <>
      <DocumentMeta title="Limit Ranges" />
      <AddLimitRangeModal addRoute={routes[routePath].limitRanges.add} />
      {showDeleteDialog && (
        <DeleteLimitRangeDialog
          onClose={() => setShowDeleteDialog(false)}
          rows={[selectedLimitRange]}
        />
      )}
      <ListContainer<ModelDataKey, SelectorModel>
        dataKey={DataKeys.LimitRanges}
        searchTargets={searchTargets}
        uniqueIdentifier="id"
        loading={loading}
        loadingMessage={message}
        onRefresh={reload}
        data={limitRanges}
        columns={columns}
        addUrl={routes[routePath].limitRanges.add.path()}
        addText="Add Limit Ranges"
        getParamsUpdater={getParamsUpdater}
        rowMenuItems={rowMenuItems}
        showItemsCountInLabel
        showRowMenuForSingleRowActions
        {...pick(listTablePrefs, params)}
      />
    </>
  )
}
